// @flow
import { createSelector, createStructuredSelector } from 'reselect';

import {
  selectCartBundles,
  selectOneProductErrors,
  selectProducts,
  selectRawProductsArray,
  type CartBundle,
  type DecoratedProduct,
  type ProductError,
  type ProductId,
  type ProductKind,
  type ProductPayload,
} from '@gandi/cart';

import { webhostingSizes } from 'src/modules/simplehosting/prices';

import type { FullState } from '../../../types';

import { normalizeMailboxProduct } from './modules/utils';

export type ByKind<T> = {
  [kind: ProductKind]: T,
};

export type KindProps = {
  productType: string,
  process: string,
};

export type ProductsByKind = ByKind<Array<DecoratedProduct>>;
export type ProductKinds = Array<ProductKind>;
export type ProductIds = Array<ProductId>;
export type CorporateMiscFees = { count: number, priceBeforeTaxes: number };
export type CorporateMiscFeesByKind = ByKind<CorporateMiscFees>;

const productKindGroups = {
  CorporateCreate: 'services',
  TmchCreate: 'services',
  TmchRenew: 'services',
} as const;
type ProductKindGroups = $Values<typeof productKindGroups>;

const selectProductsByKind: (state: FullState) => ProductsByKind =
  createSelector([selectProducts], (products): ProductsByKind => {
    return products.reduce((acc, product) => {
      const group: ProductKind =
        // $FlowFixMe[prop-missing] all good, we have a fallback just below
        (productKindGroups[product.component] as ProductKindGroups) ||
        product.component;

      if (!acc[group]) {
        acc[group] = [product];
      } else {
        acc[group].push(product);
      }

      return acc;
    }, ({}: ProductsByKind));
  });

const selectDomainCreateBundles: (state: FullState) => Array<CartBundle> =
  createSelector([selectCartBundles], (cartBundles): Array<CartBundle> => {
    return cartBundles.filter(bundle =>
      bundle.products.every(
        product =>
          product?.productType === 'domain' && product?.process === 'create',
      ),
    );
  });

export const selectProductKinds: (state: FullState) => ProductKinds =
  createSelector([selectProductsByKind], groupped =>
    Object.keys(groupped)
      // CorporateCreate component and other services always goes last
      .sort((a, b) => {
        if (a === b) {
          return 0;
        }
        if (a === 'services') {
          return 1;
        }
        if (b === 'services') {
          return -1;
        }
        if (a.startsWith('Domain') && b.startsWith('Domain')) {
          return 0;
        }
        if (a.startsWith('Domain')) {
          return -1;
        }
        if (b.startsWith('Domain')) {
          return 1;
        }
        return 0;
      }),
  );

const selectCorporateMiscFeesFactory: (
  props: KindProps,
) => FullState => CorporateMiscFees = props =>
  createSelector([selectRawProductsArray], products =>
    products
      .filter(
        product =>
          product.productType === props.productType &&
          product.process === props.process,
      )
      .reduce(
        (acc, product) => {
          const miscFeesCorporate =
            (product.options &&
              product.options.find(
                option =>
                  option.description === 'misc_fees' &&
                  option.productType === 'corporate',
              )) ||
            undefined;

          if (!miscFeesCorporate) {
            return acc;
          }

          acc.count += 1;
          acc.priceBeforeTaxes +=
            miscFeesCorporate.unitPrice ||
            // $FlowFixMe[prop-missing]
            miscFeesCorporate.total?.before_taxes ||
            0;

          return acc;
        },
        { count: 0, priceBeforeTaxes: 0 },
      ),
  );

/**
 * Search if any product has error on language for IDN.
 */
export const IDNErrorRegexp: RegExp = /^lang: mandatory for/;
export function hasIDNError(productErrors: Array<ProductError>): boolean {
  return (
    productErrors.filter(error => IDNErrorRegexp.test(error.description))
      .length > 0
  );
}

export const selectHasDomainCreateIDNErrors: (state: FullState) => boolean =
  createSelector([selectProducts, state => state], (products, state) => {
    return products
      .map(({ id: productId }) =>
        hasIDNError(selectOneProductErrors(state, { productId })),
      )
      .some(Boolean);
  });

export type SelectCorporateMiscFeesByKind = (
  state: FullState,
) => CorporateMiscFeesByKind;
export const selectCorporateMiscFeesByKind: SelectCorporateMiscFeesByKind =
  createStructuredSelector({
    DomainCreate: selectCorporateMiscFeesFactory({
      productType: 'domain',
      process: 'create',
    }),
    DomainRenew: selectCorporateMiscFeesFactory({
      productType: 'domain',
      process: 'renew',
    }),
    DomainRestore: selectCorporateMiscFeesFactory({
      productType: 'domain',
      process: 'restore',
    }),
    DomainTransfer: selectCorporateMiscFeesFactory({
      productType: 'domain',
      process: 'transfer',
    }),
  });

export const selectMoreThan1YearDuration: (
  state: FullState,
  payload: ProductPayload,
) => boolean = createSelector(
  [(_, props) => props],
  ({ duration }) => !!duration && duration > 1,
);

export type GroupedProducts = {
  firstProduct: void | DecoratedProduct,
  productItems: Array<DecoratedProduct>,
  mailboxRenewGroups: { [mailboxType: string]: Array<DecoratedProduct> },
  cartDomainCreateBundles: Array<CartBundle>,
  hasWebhosting: boolean,
};
export const selectGroupedProductsForKind: (
  state: FullState,
  props: { kind: ProductKind },
) => GroupedProducts = createSelector(
  [selectProductsByKind, selectDomainCreateBundles, (_, props) => props.kind],
  (productsByKind, allDomainCreateBundles, kind) => {
    const products = productsByKind[kind] || [];

    const cartDomainCreateBundles =
      kind === 'DomainCreate' ? allDomainCreateBundles : [];

    return products.reduce(
      (acc, product) => {
        // Ignore products that belong to a create bundle
        if (!!product.bundleId && product.process === 'create') {
          return acc;
        }

        if (
          (product.productType === 'mailboxv2' ||
            product.productType === 'mailbox-standalone') &&
          product.process === 'renew'
        ) {
          const { mailboxType } = normalizeMailboxProduct(product);

          if (!acc.mailboxRenewGroups[mailboxType]) {
            acc.mailboxRenewGroups[mailboxType] = [];
          }
          acc.mailboxRenewGroups[mailboxType].push(product);
        } else {
          acc.productItems.push(product);

          if (!acc.hasWebhosting && product.productType === 'paas') {
            if (webhostingSizes.includes(product.payload?.size)) {
              acc.hasWebhosting = true;
            }
          }
        }

        return acc;
      },
      {
        firstProduct: products[0],
        productItems: [],
        mailboxRenewGroups: {},
        cartDomainCreateBundles,
        hasWebhosting: false,
      },
    );
  },
);
